"use client";

import { useEffect, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useLoading } from "../contexts/LoadingContext";
import {
  getStockLinkFromEvent,
  isModifiedMouseEvent,
  shouldNavigateToStock,
  triggerStockLoading,
} from "@/lib/stockNavigationFeedback";

/**
 * Instant GlobalLoading on any /stock/ link click (capture + flushSync).
 * Prefetch on hover. Loading clears when StockPageClient mounts.
 */
export default function StockNavigationHandler() {
  const router = useRouter();
  const { setLoading, setLoadingMessage } = useLoading();
  const prefetched = useRef(new Set<string>());

  const showLoading = useCallback(() => {
    triggerStockLoading(setLoading, setLoadingMessage);
  }, [setLoading, setLoadingMessage]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (isModifiedMouseEvent(e)) return;

      const anchor = getStockLinkFromEvent(e.target);
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || !shouldNavigateToStock(href)) return;

      showLoading();

      queueMicrotask(() => {
        if (e.defaultPrevented) {
          setLoading(false);
        }
      });
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [showLoading, setLoading]);

  useEffect(() => {
    const onPointerOver = (e: PointerEvent) => {
      const anchor = getStockLinkFromEvent(e.target);
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || prefetched.current.has(href)) return;

      prefetched.current.add(href);
      router.prefetch(href);
    };

    document.addEventListener("pointerover", onPointerOver, true);
    return () => document.removeEventListener("pointerover", onPointerOver, true);
  }, [router]);

  return null;
}
